/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import type { syncGroupsTypes } from '@opengeoweb/store';

interface SimplifiedFeatureLayer {
  id: string;
  originalId?: string;
  geoJSON: FeatureCollection;
}

// Normalizes feature/layer ids from unknown values into string keys used in maps/sets.
const getFeatureKey = (value: unknown): string | undefined =>
  value !== undefined && value !== null ? String(value) : undefined;

// Extracts warning-dialog feature layers into a minimal shape used by map sync helpers.
// Used to sync changes in warning-dialog map to map views
export const createWarningDialogFeatureLayers = (
  warningDialogSharedData: syncGroupsTypes.PotentialData,
): SimplifiedFeatureLayer[] =>
  Object.values(warningDialogSharedData.featureLayersById ?? {}).map(
    (layer) => ({
      id: layer.layerId,
      originalId: layer.originalId,
      geoJSON: layer.geoJSON,
    }),
  );

// Builds a lookup set of all ids/originalIds present in warning-dialog layers.
// Used to prevent rendering duplicate features from linked panel layers.
export const createWarningDialogFeatureKeys = (
  warningDialogFeatures: SimplifiedFeatureLayer[],
): Set<string> => {
  const keys = new Set<string>();
  warningDialogFeatures.forEach((featureLayer) => {
    featureLayer.geoJSON.features.forEach((feature) => {
      const featureIdKey = getFeatureKey(feature.id);
      const originalIdKey = getFeatureKey(feature.properties?.originalId);
      if (featureIdKey) {
        keys.add(featureIdKey);
      }
      if (originalIdKey) {
        keys.add(originalIdKey);
      }
    });
    const layerOriginalIdKey = getFeatureKey(featureLayer.originalId);
    if (layerOriginalIdKey) {
      keys.add(layerOriginalIdKey);
    }
  });
  return keys;
};

// Creates a reverse index: feature key -> panel ids that own that feature.
// Used to route hover/selection updates to the correct linked panel(s).
export const getPanelIdsByFeatureKey = (
  linkedSharedDataByPanel: Record<string, syncGroupsTypes.PotentialData>,
): Record<string, string[]> => {
  const ownerById = new Map<string, Set<string>>();

  const addOwner = (key: string | undefined, panelId: string): void => {
    if (!key) {
      return;
    }
    const owners = ownerById.get(key) ?? new Set<string>();
    owners.add(panelId);
    ownerById.set(key, owners);
  };

  Object.entries(linkedSharedDataByPanel).forEach(([panelId, sharedData]) => {
    Object.values(sharedData.featureLayersById ?? {}).forEach((layer) => {
      addOwner(getFeatureKey(layer.originalId), panelId);
      layer.geoJSON.features.forEach((feature) => {
        addOwner(getFeatureKey(feature.id), panelId);
        addOwner(getFeatureKey(feature.properties?.originalId), panelId);
      });
    });
  });

  const ownersByKey: Record<string, string[]> = {};
  ownerById.forEach((owners, key) => {
    ownersByKey[key] = Array.from(owners);
  });
  return ownersByKey;
};

// Map selections must only update panels directly linked to the clicked map.
// Linked maps can form a transitive graph, but using the full graph here would
// update panels that were explicitly unlinked from the clicked map.
export const getFeatureSelectionTargetPanelIds = (
  selectedFeatureId: string,
  featureOwnerPanelIdsById: Record<string, string[]>,
  directlyLinkedPanelIds: string[],
): string[] => {
  const directlyLinkedPanelIdSet = new Set(directlyLinkedPanelIds);
  const ownerPanelIds = selectedFeatureId
    ? (featureOwnerPanelIdsById[selectedFeatureId] ?? [])
    : [];

  if (ownerPanelIds.length > 0) {
    return ownerPanelIds.filter((panelId) =>
      directlyLinkedPanelIdSet.has(panelId),
    );
  }

  if (directlyLinkedPanelIds.length === 1) {
    return [directlyLinkedPanelIds[0]];
  }

  return [];
};

// Merges linked panel feature layers into one collection for map rendering,
// while filtering out features that are already represented by warning-dialog layers.
export const createLinkedFeatureCollection = (
  linkedFeatureLayers: syncGroupsTypes.FeatureLayerData[],
  warningDialogFeatureKeys: Set<string>,
): FeatureCollection => ({
  type: 'FeatureCollection',
  features: linkedFeatureLayers.flatMap((featureLayer) =>
    featureLayer.geoJSON.features
      .filter((geoFeature) => {
        const featureId = getFeatureKey(geoFeature.id);
        const originalId =
          getFeatureKey(geoFeature.properties?.originalId) ||
          getFeatureKey(featureLayer.originalId);
        return !(
          (featureId && warningDialogFeatureKeys.has(featureId)) ||
          (originalId && warningDialogFeatureKeys.has(originalId))
        );
      })
      .map((geoFeature) => ({
        ...geoFeature,
        properties: {
          ...geoFeature.properties,
          ...(featureLayer.originalId
            ? { originalId: featureLayer.originalId }
            : undefined),
        },
      })),
  ),
});
